import { useCallback, useRef, useState } from 'react';

/**
 * useUndoRedo Hook - Command/Delta-based with Batch Support
 * 
 * Instead of storing full state snapshots, stores actions (commands) with their inverses.
 * Each action knows how to undo itself.
 * 
 * BATCH SUPPORT:
 * - Use startBatch() before multiple related operations
 * - Use endBatch() after all operations complete
 * - All actions recorded during a batch become a single expandable undo entry
 * 
 * Session-only (in-memory) - clears on refresh, which is standard behavior.
 * 
 * Uses refs for the actual stacks to avoid race conditions, and useState for UI reactivity.
 * 
 * @param {number} maxHistory - Maximum number of undo steps (default: 50)
 */
export function useUndoRedo(maxHistory = 50) {
  // Refs for the actual stacks (source of truth - avoids closure stale state issues)
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  
  // State for UI reactivity (mirrors the refs)
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  
  // Lock to prevent concurrent operations and recording during undo/redo
  const isLockedRef = useRef(false);
  
  // Batch state - when active, actions are collected instead of added directly
  const batchRef = useRef(null); // { type, description, actions: [] } or null

  // Helper to sync refs to state
  const syncToState = useCallback(() => {
    setUndoStack([...undoStackRef.current]);
    setRedoStack([...redoStackRef.current]);
  }, []);

  /**
   * Start a batch - all subsequent recordAction calls will be grouped
   * @param {string} type - The batch action type (e.g., 'MOVE_NODES')
   * @param {string} description - Description for the batch (e.g., 'Move 3 items')
   */
  const startBatch = useCallback((type, description) => {
    if (isLockedRef.current) return;
    batchRef.current = {
      type,
      description,
      actions: [],
    };
  }, []);

  /**
   * End a batch - commits all collected actions as a single undo entry
   */
  const endBatch = useCallback(() => {
    if (isLockedRef.current || !batchRef.current) return;
    
    const batch = batchRef.current;
    batchRef.current = null;
    
    // If no actions were recorded, nothing to commit
    if (batch.actions.length === 0) return;
    
    // Create a grouped action that undoes/redoes all sub-actions
    const groupedAction = {
      type: batch.type,
      description: batch.description,
      isGroup: true,
      subActions: batch.actions,
      timestamp: Date.now(),
      id: `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      // Undo all sub-actions in reverse order
      undo: () => {
        for (let i = batch.actions.length - 1; i >= 0; i--) {
          batch.actions[i].undo();
        }
      },
      // Redo all sub-actions in original order
      redo: () => {
        for (let i = 0; i < batch.actions.length; i++) {
          batch.actions[i].redo();
        }
      },
    };

    // Add to undo stack
    undoStackRef.current = [...undoStackRef.current, groupedAction];
    if (undoStackRef.current.length > maxHistory) {
      undoStackRef.current.shift();
    }
    
    // Clear redo stack on new action
    redoStackRef.current = [];
    
    syncToState();
  }, [maxHistory, syncToState]);

  /**
   * Cancel a batch without committing
   */
  const cancelBatch = useCallback(() => {
    batchRef.current = null;
  }, []);

  /**
   * Record an action for undo
   * If a batch is active, adds to the batch instead of directly to the stack
   */
  const recordAction = useCallback((action) => {
    // Don't record during undo/redo operations
    if (isLockedRef.current) {
      return;
    }

    // Validate action
    if (!action.undo || typeof action.undo !== 'function') {
      console.error('[UNDO] Invalid action: missing undo function', action);
      return;
    }
    if (!action.redo || typeof action.redo !== 'function') {
      console.error('[UNDO] Invalid action: missing redo function', action);
      return;
    }

    // Add timestamp for display
    const actionWithMeta = {
      ...action,
      timestamp: Date.now(),
      id: `action-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    // If batch is active, collect instead of committing
    if (batchRef.current) {
      batchRef.current.actions.push(actionWithMeta);
      return;
    }

    // Update ref (source of truth)
    undoStackRef.current = [...undoStackRef.current, actionWithMeta];
    if (undoStackRef.current.length > maxHistory) {
      undoStackRef.current.shift();
    }
    
    // Clear redo stack on new action (standard behavior)
    redoStackRef.current = [];
    
    // Sync to state for UI
    syncToState();
  }, [maxHistory, syncToState]);

  // Undo: execute the undo function of the last action
  const undo = useCallback(() => {
    // Check lock and stack
    if (isLockedRef.current) return false;
    if (undoStackRef.current.length === 0) return false;
    
    // Lock
    isLockedRef.current = true;
    
    // Get the last action from ref
    const action = undoStackRef.current[undoStackRef.current.length - 1];
    
    try {
      // Execute the undo function
      action.undo();
      
      // Update refs
      undoStackRef.current = undoStackRef.current.slice(0, -1);
      redoStackRef.current = [...redoStackRef.current, action];
      
      // Sync to state
      syncToState();
    } catch (error) {
      console.error('[UNDO] Error executing undo:', error);
    }
    
    // Unlock after a microtask to let React settle
    Promise.resolve().then(() => {
      isLockedRef.current = false;
    });
    
    return true;
  }, [syncToState]);

  // Redo: execute the redo function of the last undone action
  const redo = useCallback(() => {
    // Check lock and stack
    if (isLockedRef.current) return false;
    if (redoStackRef.current.length === 0) return false;
    
    // Lock
    isLockedRef.current = true;
    
    // Get the last undone action from ref
    const action = redoStackRef.current[redoStackRef.current.length - 1];
    
    try {
      // Execute the redo function
      action.redo();
      
      // Update refs
      redoStackRef.current = redoStackRef.current.slice(0, -1);
      undoStackRef.current = [...undoStackRef.current, action];
      
      // Sync to state
      syncToState();
    } catch (error) {
      console.error('[REDO] Error executing redo:', error);
    }
    
    // Unlock after a microtask
    Promise.resolve().then(() => {
      isLockedRef.current = false;
    });
    
    return true;
  }, [syncToState]);

  // Jump to a specific point in history (undo multiple at once)
  const undoTo = useCallback((actionId) => {
    // Check lock
    if (isLockedRef.current) return false;
    
    const index = undoStackRef.current.findIndex(a => a.id === actionId);
    if (index === -1) return false;
    
    // Lock
    isLockedRef.current = true;
    
    // Get actions to undo from ref
    const actionsToUndo = undoStackRef.current.slice(index);
    
    // Execute undos in reverse order (most recent first)
    for (let i = actionsToUndo.length - 1; i >= 0; i--) {
      try {
        actionsToUndo[i].undo();
      } catch (error) {
        console.error('[UNDO_TO] Error:', error);
      }
    }
    
    // Update refs
    const reversedActions = [...actionsToUndo].reverse();
    undoStackRef.current = undoStackRef.current.slice(0, index);
    redoStackRef.current = [...redoStackRef.current, ...reversedActions];
    
    // Sync to state
    syncToState();
    
    // Unlock after a microtask
    Promise.resolve().then(() => {
      isLockedRef.current = false;
    });
    
    return true;
  }, [syncToState]);

  // Jump forward to a specific point in history (redo multiple at once)
  const redoTo = useCallback((actionId) => {
    // Check lock
    if (isLockedRef.current) return false;
    
    const index = redoStackRef.current.findIndex(a => a.id === actionId);
    if (index === -1) return false;
    
    // Lock
    isLockedRef.current = true;
    
    // Redo stack is stored in reverse chronological order:
    // [MOVE_3, MOVE_2, MOVE_1, ADD_4, ADD_3, ADD_2] where ADD_2 was done first
    // 
    // When clicking ADD_2 (index 5), we want to redo from ADD_2 up to the most recent undone
    // So we need items from index to end, and execute them in REVERSE order (end first = ADD_2 first)
    const actionsToRedo = redoStackRef.current.slice(index);
    
    // Execute redos in REVERSE order (last in array = earliest chronologically = redo first)
    for (let i = actionsToRedo.length - 1; i >= 0; i--) {
      try {
        actionsToRedo[i].redo();
      } catch (error) {
        console.error('[REDO_TO] Error:', error);
      }
    }
    
    // Update refs - add to undo stack in correct order (earliest first)
    // actionsToRedo is [MOVE_3...ADD_2], reversed = [ADD_2...MOVE_3] which is chronological
    const chronologicalOrder = [...actionsToRedo].reverse();
    redoStackRef.current = redoStackRef.current.slice(0, index);
    undoStackRef.current = [...undoStackRef.current, ...chronologicalOrder];
    
    // Sync to state
    syncToState();
    
    // Unlock after a microtask
    Promise.resolve().then(() => {
      isLockedRef.current = false;
    });
    
    return true;
  }, [syncToState]);

  // Undo all actions (return to original state)
  const undoAll = useCallback(() => {
    if (isLockedRef.current) return false;
    if (undoStackRef.current.length === 0) return false;
    
    isLockedRef.current = true;
    
    const allActions = [...undoStackRef.current];
    
    // Execute undos in reverse order (most recent first)
    for (let i = allActions.length - 1; i >= 0; i--) {
      try {
        allActions[i].undo();
      } catch (error) {
        console.error('[UNDO_ALL] Error:', error);
      }
    }
    
    // Move all to redo stack (reversed for proper order)
    undoStackRef.current = [];
    redoStackRef.current = [...redoStackRef.current, ...allActions.reverse()];
    
    syncToState();
    
    Promise.resolve().then(() => {
      isLockedRef.current = false;
    });
    
    return true;
  }, [syncToState]);

  // Clear history (useful when switching campaigns)
  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    syncToState();
  }, [syncToState]);

  // Check if currently locked (for external use)
  const isUndoRedoing = useCallback(() => {
    return isLockedRef.current;
  }, []);

  /**
   * Wrap the most recent undo action (if predicate passes) with new undo/redo behavior.
   * Used to combine delete + layout compaction into a single undo step.
   */
  const wrapLastUndoAction = useCallback((predicate, enhance) => {
    if (isLockedRef.current) return false;

    const stack = undoStackRef.current;
    if (stack.length === 0) return false;

    const idx = stack.length - 1;
    const last = stack[idx];
    if (predicate && !predicate(last)) return false;

    const originalUndo = last.undo;
    const originalRedo = last.redo;
    const wrapped = enhance({ originalUndo, originalRedo });

    if (!wrapped?.undo || !wrapped?.redo) return false;

    stack[idx] = {
      ...last,
      undo: wrapped.undo,
      redo: wrapped.redo,
      description: wrapped.description || last.description,
    };

    syncToState();
    return true;
  }, [syncToState]);

  return {
    recordAction,
    wrapLastUndoAction,
    undo,
    redo,
    undoTo,
    redoTo,
    undoAll,
    clearHistory,
    isUndoRedoing,
    // Batch operations
    startBatch,
    endBatch,
    cancelBatch,
    // Direct access to stacks for UI display
    undoStack,
    redoStack,
    // Convenience getters
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undoCount: undoStack.length,
    redoCount: redoStack.length,
  };
}

export default useUndoRedo;
